import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import StatCard from '../dashboard/components/StatCard';
import InventoryTable from './components/InventoryTable';
import StockAdjustmentModal from './components/StockAdjustmentModal';
import LocationTrackingPanel from './components/LocationTrackingPanel';
import RecentMovementsPanel from './components/RecentMovementsPanel';

const InventoryTrackingDashboard = () => {
  const [products, setProducts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isAdjustmentModalOpen, setIsAdjustmentModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [customerLocationFilter, setCustomerLocationFilter] = useState('all');
  const [customerLocationOptions, setCustomerLocationOptions] = useState([]);

  // Fetch all data
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch products
      const { data: productsData, error: productsError } = await supabase?.from('products')?.select('*')?.eq('is_trackable', true)?.order('name', { ascending: true });

      if (productsError) throw productsError;

      // Fetch recent transactions
      const { data: transactionsData, error: transactionsError } = await supabase?.from('inventory_transactions')?.select('*, products(name, sku)')?.order('created_at', { ascending: false })?.limit(20);

      if (transactionsError) throw transactionsError;

      // Fetch locations
      const { data: locationsData, error: locationsError } = await supabase?.from('inventory_locations')?.select('*, products(name, sku)')?.order('last_updated', { ascending: false });

      if (locationsError) throw locationsError;

      // Fetch customer locations for filter
      const { data: customerLocData } = await supabase
        ?.from('customer_locations')
        ?.select('id, well_name, company')
        ?.order('well_name');

      setProducts(productsData || []);
      setTransactions(transactionsData || []);
      setLocations(locationsData || []);
      setCustomerLocationOptions((customerLocData || [])?.map(loc => ({
        value: loc?.id,
        label: loc?.well_name,
        company: loc?.company
      })));
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err?.message || 'Failed to load inventory data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    // Real-time subscription for products
    const productsChannel = supabase?.channel('products_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'products'
        },
        (payload) => {
          if (payload?.eventType === 'INSERT') {
            setProducts(prev => [...prev, payload?.new]);
          } else if (payload?.eventType === 'UPDATE') {
            setProducts(prev => prev?.map(p => p?.id === payload?.new?.id ? payload?.new : p));
          } else if (payload?.eventType === 'DELETE') {
            setProducts(prev => prev?.filter(p => p?.id !== payload?.old?.id));
          }
        }
      )?.subscribe();

    // Real-time subscription for transactions
    const transactionsChannel = supabase?.channel('transactions_changes')?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'inventory_transactions'
        },
        async (payload) => {
          // Fetch product details for new transaction
          const { data: productData } = await supabase?.from('products')?.select('name, sku')?.eq('id', payload?.new?.product_id)?.single();

          const newTransaction = {
            ...payload?.new,
            products: productData
          };

          setTransactions(prev => [newTransaction, ...prev]?.slice(0, 20));
        }
      )?.subscribe();

    // Real-time subscription for locations
    const locationsChannel = supabase?.channel('locations_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'inventory_locations'
        },
        async (payload) => {
          if (payload?.eventType === 'INSERT' || payload?.eventType === 'UPDATE') {
            const { data: productData } = await supabase?.from('products')?.select('name, sku')?.eq('id', payload?.new?.product_id)?.single();

            const updatedLocation = {
              ...payload?.new,
              products: productData
            };

            if (payload?.eventType === 'INSERT') {
              setLocations(prev => [updatedLocation, ...prev]);
            } else {
              setLocations(prev => prev?.map(l => l?.id === updatedLocation?.id ? updatedLocation : l));
            }
          } else if (payload?.eventType === 'DELETE') {
            setLocations(prev => prev?.filter(l => l?.id !== payload?.old?.id));
          }
        }
      )?.subscribe();

    // Cleanup subscriptions
    return () => {
      supabase?.removeChannel(productsChannel);
      supabase?.removeChannel(transactionsChannel);
      supabase?.removeChannel(locationsChannel);
    };
  }, []);

  // Calculate statistics
  const stats = {
    totalStock: products?.reduce((sum, p) => sum + (p?.quantity || 0), 0),
    lowStockCount: products?.filter(p => p?.status === 'Low Stock')?.length,
    outOfStockCount: products?.filter(p => p?.status === 'Out of Stock')?.length,
    inTransit: transactions?.filter(t => t?.transaction_type === 'transfer')?.length
  };

  // Filter products
  const filteredProducts = products?.filter(product => {
    const matchesSearch = product?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         product?.sku?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || product?.category === categoryFilter;
    const matchesStatus = statusFilter === 'all' || product?.status === statusFilter;
    const matchesLocation = customerLocationFilter === 'all' || product?.customer_location_id === customerLocationFilter;
    return matchesSearch && matchesCategory && matchesStatus && matchesLocation;
  });

  const handleStockAdjustment = (product) => {
    setSelectedProduct(product);
    setIsAdjustmentModalOpen(true);
  };

  const handleAdjustmentSave = async (adjustmentData) => {
    try {
      const product = selectedProduct;
      const newQuantity = product?.quantity + adjustmentData?.quantityChange;

      // Update product quantity
      const { error: updateError } = await supabase?.from('products')?.update({ 
          quantity: newQuantity,
          last_restocked: adjustmentData?.transaction_type === 'stock_in' ? new Date()?.toISOString() : product?.last_restocked
        })?.eq('id', product?.id);

      if (updateError) throw updateError;

      // Create transaction record
      const { error: transactionError } = await supabase?.from('inventory_transactions')?.insert({
          product_id: product?.id,
          transaction_type: adjustmentData?.transaction_type,
          quantity: Math.abs(adjustmentData?.quantityChange),
          previous_quantity: product?.quantity,
          new_quantity: newQuantity,
          location: adjustmentData?.location,
          assigned_technician: adjustmentData?.assignedTechnician,
          notes: adjustmentData?.notes
        });

      if (transactionError) throw transactionError;

      // Update or create location record
      if (adjustmentData?.location) {
        const { data: existingLocation } = await supabase?.from('inventory_locations')?.select('*')?.eq('product_id', product?.id)?.eq('location_name', adjustmentData?.location)?.maybeSingle();

        if (existingLocation) {
          await supabase?.from('inventory_locations')?.update({
              quantity: existingLocation?.quantity + adjustmentData?.quantityChange,
              assigned_technician: adjustmentData?.assignedTechnician,
              last_updated: new Date()?.toISOString()
            })?.eq('id', existingLocation?.id);
        } else {
          await supabase?.from('inventory_locations')?.insert({
              product_id: product?.id,
              location_name: adjustmentData?.location,
              quantity: adjustmentData?.quantityChange,
              assigned_technician: adjustmentData?.assignedTechnician
            });
        }
      }

      setIsAdjustmentModalOpen(false);
      setSelectedProduct(null);
    } catch (err) {
      console.error('Error saving adjustment:', err);
      alert(err?.message || 'Failed to save stock adjustment');
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading inventory data...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-background p-4 md:p-6 lg:p-8">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
            Inventory Tracking Dashboard
          </h1>
          <p className="text-muted-foreground">
            Real-time monitoring of trackable products and equipment
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive rounded-lg">
            <p className="text-destructive font-medium">{error}</p>
          </div>
        )}

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6">
          <StatCard
            title="Total Stock Level"
            value={stats?.totalStock}
            icon="Package"
            iconColor="var(--color-primary)"
            subtitle="All trackable items"
            trend={0}
            trendDirection="neutral"
            onClick={() => {}}
          />
          <StatCard
            title="Low Stock Alerts"
            value={stats?.lowStockCount}
            icon="AlertTriangle"
            iconColor="var(--color-warning)"
            subtitle="Below reorder level"
            trend={0}
            trendDirection="neutral"
            onClick={() => {}}
          />
          <StatCard
            title="Out of Stock"
            value={stats?.outOfStockCount}
            icon="XCircle"
            iconColor="var(--color-error)"
            subtitle="Requires immediate reorder"
            trend={0}
            trendDirection="neutral"
            onClick={() => {}}
          />
          <StatCard
            title="Items in Transit"
            value={stats?.inTransit}
            icon="Truck"
            iconColor="var(--color-info)"
            subtitle="Currently being transferred"
            trend={0}
            trendDirection="neutral"
            onClick={() => {}}
          />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Inventory Table - Takes 2 columns */}
          <div className="lg:col-span-2">
            <div className="bg-card rounded-xl border border-border shadow-elevation-1 p-4 md:p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <h2 className="text-xl font-heading font-semibold text-foreground">
                  Product Inventory
                </h2>
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative">
                    <Icon
                      name="Search"
                      size={18}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                    />
                    <input
                      type="text"
                      placeholder="Search products..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e?.target?.value)}
                      className="pl-10 pr-4 py-2 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring w-full sm:w-64"
                    />
                  </div>
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e?.target?.value)}
                    className="px-4 py-2 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="all">All Categories</option>
                    <option value="HVAC">HVAC</option>
                    <option value="Plumbing">Plumbing</option>
                    <option value="Electrical">Electrical</option>
                    <option value="Refrigeration">Refrigeration</option>
                    <option value="Tools">Tools</option>
                    <option value="Safety Equipment">Safety Equipment</option>
                  </select>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e?.target?.value)}
                    className="px-4 py-2 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="all">All Status</option>
                    <option value="In Stock">In Stock</option>
                    <option value="Low Stock">Low Stock</option>
                    <option value="Out of Stock">Out of Stock</option>
                  </select>
                  <select
                    value={customerLocationFilter}
                    onChange={(e) => setCustomerLocationFilter(e?.target?.value)}
                    className="px-4 py-2 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="all">All Locations</option>
                    {customerLocationOptions?.map(loc => (
                      <option key={loc?.value} value={loc?.value}>
                        {loc?.label}{loc?.company ? ` (${loc?.company})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <InventoryTable
                products={filteredProducts}
                onAdjustStock={handleStockAdjustment}
              />
            </div>
          </div>

          {/* Right Sidebar - Takes 1 column */}
          <div className="space-y-6">
            <LocationTrackingPanel locations={locations} />
            <RecentMovementsPanel transactions={transactions} />
          </div>
        </div>
      </div>

      {/* Stock Adjustment Modal */}
      {isAdjustmentModalOpen && (
        <StockAdjustmentModal
          product={selectedProduct}
          onClose={() => {
            setIsAdjustmentModalOpen(false);
            setSelectedProduct(null);
          }}
          onSave={handleAdjustmentSave}
        />
      )}
    </>
  );
};

export default InventoryTrackingDashboard;